import { Injectable } from '@angular/core';
import { fork as radashFork } from 'radash';

import { SynopticStandardUtils } from '@connect-services/client/components/synoptic/synoptic-graph/synoptic-standard-color.utils';
import { ColorEnum } from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.constants';
import {
    Cable,
    Cables,
    Cassettes,
    Drawer,
    Drawers,
    PositionEnum,
    SynopticFilterOption,
    SynopticPlanItem,
    Tube,
} from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.models';
import { AssertService } from '@connect-services/client/services/assert.service';
import { PlanTypeEnum, SynopticBayPlanItem, SynopticBoxPlanItem } from '@connect-services/src/sdk/api';

@Injectable({
    providedIn: 'root',
})
export class SynopticPlanBuilderService {
    public fillUpFilter(nodeType: string, data: Drawers | Cassettes<SynopticBoxPlanItem>): SynopticFilterOption[] {
        const options: SynopticFilterOption[] = [];
        if (nodeType === PlanTypeEnum.BAY) {
            Object.keys(data).forEach((key) => {
                const drawer = data[key] as Drawer;
                const cassettes: Cassettes<SynopticBayPlanItem> = drawer.cassettes;
                Object.keys(cassettes).forEach((cassetteKey: string): void => {
                    const cassette: SynopticBayPlanItem[] = cassettes[cassetteKey];
                    const numero = parseInt(cassette[0].numero_cassette || '0');

                    options.push({ drawer: key, key: cassetteKey, numero, text: `${numero} - ${cassetteKey}` });
                });
            });
        } else {
            Object.keys(data).forEach((key) => {
                const cassette = data[key] as SynopticBoxPlanItem[];
                const numero = parseInt(cassette[0].numero_cassette || '0');

                options.push({ key, numero, text: `${numero} - ${key}` });
            });
        }

        const [physicalCassettes, virtualCassettes] = radashFork(
            options,
            (option: SynopticFilterOption) => !!option.numero,
        );

        return [
            ...physicalCassettes.sort((a: SynopticFilterOption, b: SynopticFilterOption) => a.numero - b.numero),
            ...virtualCassettes,
        ];
    }

    // The info for each cable is extracted from each cassette
    // Then the info for each tube is extracted from each cable
    public groupByCables(cassettes: Cassettes<SynopticPlanItem>): Cables {
        const sides: ('entrant' | 'sortant')[] = ['entrant', 'sortant'];
        let cables: Cables = {};

        sides.forEach((side) => {
            Object.keys(cassettes).forEach((key) => {
                const cassette = cassettes[key];

                const cablesRes = cassette.reduce(
                    (acc, current) => {
                        const cable = current[`cable_${side}`] || '';

                        const item = current[`boite_baie_${side}e`];

                        if (!acc[cable] && cable) {
                            acc[cable] = {
                                boiteBaie: item,
                                boiteBaieCodeext: current[`boite_baie_codeext_${side}e`] || null,
                                boiteBaieEtiquet: current[`boite_baie_etiquet_${side}e`] || null,
                                codeext: current[`cable_codeext_${side}`],
                                etiquet: current[`cable_etiquet_${side}`],
                                tubes: {} as Record<string, Tube>,
                            };
                        }

                        return acc;
                    },
                    {} as Record<string, Cable>,
                );

                cables = { ...cables, [key]: { ...cables[key], [side]: cablesRes } };

                Object.keys(cablesRes).forEach((cableKey) => {
                    const tubes = cassette.filter((c) => c[`cable_${side}`] === cableKey);
                    cables[key][side][cableKey].tubes = tubes.reduce(
                        (acc: Record<string, Tube>, item: SynopticPlanItem) => {
                            const numeroTube = item[`numero_tube_${side}`];
                            if (!numeroTube) {
                                return {};
                            }

                            const strNumTub = numeroTube.toString();
                            const color =
                                SynopticStandardUtils.formatColor(Number(item[`numero_tube_${side}`])) ??
                                ColorEnum.BLACK;
                            AssertService.isEnum<ColorEnum>(color, ColorEnum);

                            if (!acc?.[strNumTub]) {
                                acc = {
                                    ...acc,
                                    [strNumTub]: {
                                        color: color,
                                        typeFibre: item[`type_fibre_${side}e`] ?? '',
                                    },
                                };
                            }

                            return acc;
                        },
                        {},
                    );
                });
            });
        });

        return cables;
    }

    public groupByCassette(result: SynopticPlanItem[]): Cassettes<SynopticPlanItem> {
        return result.reduce(
            (
                cassetes: Record<string, SynopticPlanItem[]>,
                item: SynopticPlanItem,
            ): Record<string, SynopticPlanItem[]> => {
                const cassette = item.cassette || '';
                if (cassetes[cassette]) {
                    cassetes[cassette].push(item);
                } else {
                    cassetes[cassette] = [item];
                }

                return cassetes;
            },
            {},
        );
    }

    public groupByDrawer(result: SynopticBayPlanItem[]): Drawers {
        const drawers: Record<string, Drawer> = result.reduce(
            (acc: Record<string, Drawer>, current: SynopticBayPlanItem): Record<string, Drawer> => {
                const tiroir = current.tiroir || '';
                if (!acc?.[tiroir]) {
                    acc = {
                        ...acc,
                        [tiroir]: {
                            cassettes: {},
                            position: current.position_tiroir,
                            taille: current.taille_tiroir,
                        },
                    };
                }

                return acc;
            },
            {},
        );

        const groupByDrawers: Record<string, SynopticBayPlanItem[]> = result.reduce(
            (
                acc: Record<string, SynopticBayPlanItem[]>,
                current: SynopticBayPlanItem,
            ): Record<string, SynopticBayPlanItem[]> => {
                const tiroir = current.tiroir || '';
                acc[tiroir] = acc[tiroir] || [];
                acc[tiroir].push(current);

                return acc;
            },
            {},
        );

        Object.keys(groupByDrawers).forEach((drawerKey) => {
            const drawer: SynopticBayPlanItem[] = groupByDrawers[drawerKey];
            drawers[drawerKey].cassettes = this.groupByCassette(drawer) as Cassettes<SynopticBayPlanItem>;
        });

        return drawers;
    }

    public sortByPosition(position: PositionEnum): (a: SynopticPlanItem, b: SynopticPlanItem) => number {
        return (a: SynopticPlanItem, b: SynopticPlanItem): number => {
            const numeroTubeA: number = parseInt(a[`numero_tube_${position}`]!);
            const numeroTubeB: number = parseInt(b[`numero_tube_${position}`]!);

            AssertService.isNumber(numeroTubeA, 'a.numero_tube_entrant should be a number');
            AssertService.isNumber(numeroTubeB, 'b.numero_tube_entrant should be a number');

            if (a.cable_sortant === b.cable_sortant) {
                if (numeroTubeA === numeroTubeB) {
                    const numeroFibreA: number = parseInt(a[`numero_fibre_${position}e`]!);
                    const numeroFibreB: number = parseInt(b[`numero_fibre_${position}e`]!);

                    AssertService.isNumber(numeroFibreA, 'a.numero_fibre_entrante should be a number');
                    AssertService.isNumber(numeroFibreB, 'b.numero_fibre_entrante should be a number');

                    return numeroFibreA > numeroFibreB ? 1 : numeroFibreB > numeroFibreA ? -1 : 0;
                }

                return numeroTubeA > numeroTubeB ? 1 : numeroTubeB > numeroTubeA ? -1 : 0;
            }

            return numeroTubeA - numeroTubeB;
        };
    }

    /**
     * Sort cassettes by cassette number
     * @param cassettes
     */
    public sortCassettes(cassettes: Cassettes<SynopticPlanItem>): SynopticPlanItem[][] {
        const [physicalCassettes, virtualCassettes] = radashFork(
            Object.values(cassettes),
            (items: SynopticPlanItem[]) => {
                return !!parseInt(items[0].numero_cassette ?? '0');
            },
        );

        return [
            ...physicalCassettes.sort((a: SynopticPlanItem[], b: SynopticPlanItem[]) => {
                return parseInt(a[0].numero_cassette!) - parseInt(b[0].numero_cassette!);
            }),
            // Add virtual cassette at the end
            ...virtualCassettes,
        ].map((cassettes: SynopticPlanItem[]) => {
            return cassettes.sort(
                (a: SynopticPlanItem, b: SynopticPlanItem) =>
                    Number(a.numero_position ?? 0) - Number(b.numero_position ?? 0),
            );
        });
    }
}
